<script lang="ts" setup>
import { computed, provide, reactive, ref } from 'vue'
import LineAlertTriangle from '../../icons/LineAlertTriangle.vue'
import { DTFieldProvider } from '../../keys'
import type { FieldProvider } from '../../types/field'
import type { FieldInputSizes } from './types'

const props = defineProps<{
  label?: string
  labelFor?: string
  description?: string
  error?: string
  size?: keyof typeof FieldInputSizes
}>()

const inputId = computed(() => props.labelFor ?? `field-${crypto.randomUUID()}`)
const hasRequiredInput = ref(false)

const computedLabel = computed(
  () => (hasRequiredInput.value && props.label) ? `${props.label}*` : props.label,
)
const descriptionId = computed(
  () => props.description ? `field-description-${inputId.value}` : undefined,
)

const fieldProvider: FieldProvider = reactive({
  inputId,
  descriptionId,
  size: props.size,
  hasRequiredInput,
})
provide(DTFieldProvider, fieldProvider)
</script>

<template>
  <div class="flex flex-col gap-1.5 antialiased">
    <slot
      name="label"
      :computed-label="computedLabel"
      :input-id="inputId"
    >
      <div class="flex items-center gap-2">
        <DTFieldLabel
          :label-for="inputId"
          :label="computedLabel"
        />
        <slot name="append-label" />
      </div>
    </slot>

    <slot />

    <div v-if="error || description">
      <p
        v-if="description"
        :id="descriptionId"
        class="text-xs text-gray-500"
      >
        {{ description }}
      </p>
      <p
        v-if="error"
        class="flex items-center gap-1 text-xs text-red-500"
      >
        <LineAlertTriangle class="w-3 shrink-0" />
        {{ error }}
      </p>
    </div>
  </div>
</template>
